const PUBLIC_TRANSPORT_MODES = ['BICYCLE_RENT', 'BUS', 'SUBWAY', 'RAIL']
const PRIVATE_TRANSPORT_ORDER = ['CAR', 'MOTORCYCLE', 'BICYCLE', 'WALK']
const PLANNER_BIKE_OPERATOR_ID = 'bike'

function isRentedBikeStep(step) {
  return step.mode === 'BICYCLE_RENT' || (step.mode === 'BICYCLE' && step.rentedBike)
}

function isPublicTransportRoute(route) {
  return route.leg.steps.some(step => PUBLIC_TRANSPORT_MODES.includes(step.mode) || isRentedBikeStep(step))
}

export function managePlannerStops(stops) {
  stops.forEach(stop => { stop.id = stop._id })
  return stops
}

export function getPlannerRouteErrorKey(error, allRoutes) {
  return error.id === 400 && allRoutes.length === 0
    ? 'planner.noRoute'
    : 'planner.errorServer'
}

export function removeDuplicatePlannerOperators(operators) {
  const uniqueOperators = []
  const operatorKeys = new Set()

  operators.forEach(operator => {
    const key = `${operator.agency_id}-${operator.business_name}`
    if (!operatorKeys.has(key)) {
      operatorKeys.add(key)
      uniqueOperators.push(operator)
    }
  })

  return uniqueOperators
}

export function formatPlannerStopName(name) {
  let formattedName = name.replace(/_/g, ' ')
  formattedName = formattedName.toLowerCase().replace(/\b\w/g, c => c.toUpperCase())
  return formattedName
}

export function sortPlannerRoutes(routes) {
  const uniqueRoutes = Array.from(
    new Map(
      routes.map(route => [
        `${route.time}-${route.leg.steps.map(step => step.mode).join(',')}`,
        route
      ])
    ).values()
  )

  const publicTransportRoutes = uniqueRoutes.filter(route =>
    isPublicTransportRoute(route)
  )
  const publicTransportRoutesWithoutBikeRent = publicTransportRoutes.filter(route =>
    !route.leg.steps.some(step => isRentedBikeStep(step))
  )
  const publicTransportRoutesWithBikeRent = publicTransportRoutes.filter(route =>
    route.leg.steps.some(step => isRentedBikeStep(step))
  )
  const taxiRoutes = uniqueRoutes.filter(route =>
    route.leg.steps.some(step => step.mode === 'TAXI')
  )
  const privateTransportRoutes = uniqueRoutes.filter(route =>
    !route.leg.steps.some(step => PUBLIC_TRANSPORT_MODES.includes(step.mode) || step.mode === 'TAXI' || isRentedBikeStep(step))
  )

  privateTransportRoutes.sort((a, b) => {
    const aMode = a.leg.steps.find(step => PRIVATE_TRANSPORT_ORDER.includes(step.mode)).mode
    const bMode = b.leg.steps.find(step => PRIVATE_TRANSPORT_ORDER.includes(step.mode)).mode
    return PRIVATE_TRANSPORT_ORDER.indexOf(aMode) - PRIVATE_TRANSPORT_ORDER.indexOf(bMode)
  })

  return [
    ...publicTransportRoutesWithoutBikeRent,
    ...publicTransportRoutesWithBikeRent,
    ...taxiRoutes,
    ...privateTransportRoutes
  ]
}

export function shouldUsePlannerBikeStops(appConfig) {
  return appConfig.planner_motor === 'opentripplanner' && appConfig.planner_motor_url !== ''
}

export function buildPlannerBikeOperator(bikeLogo) {
  return {
    route_ids: ['111111'],
    id: PLANNER_BIKE_OPERATOR_ID,
    operator_type: PLANNER_BIKE_OPERATOR_ID,
    name: 'Valenbisi',
    business_name: 'Valenbisi',
    selected: true,
    img_url: {
      url: bikeLogo
    },
    logo_url: {
      url: bikeLogo
    }
  }
}

export function buildPlannerBikeStopsToAdd(operators, bikeStops, bikeLogo, formatName) {
  if (!operators.some(operator => operator.id === PLANNER_BIKE_OPERATOR_ID && operator.selected)) {
    return []
  }

  return bikeStops.map(bikeStop => ({
    route_ids: ['111111'],
    id: bikeStop.id,
    operator_type: PLANNER_BIKE_OPERATOR_ID,
    name: formatName(bikeStop.name),
    lat: bikeStop.y,
    lng: bikeStop.x,
    img_url: bikeLogo,
    logo_url: bikeLogo,
    bikesAvailable: bikeStop.bikesAvailable,
    spacesAvailable: bikeStop.spacesAvailable
  }))
}

export function filterPlannerStopsByOperatorRoutes(stopsAllOperators, operators) {
  const allOperatorRouteIds = operators.flatMap(operator =>
    Array.isArray(operator.route_ids) ? operator.route_ids : []
  )

  const filteredStops = stopsAllOperators.filter(stop =>
    Array.isArray(stop.route_ids) &&
    stop.route_ids.some(routeId => allOperatorRouteIds.includes(routeId))
  )

  filteredStops.forEach(stop => {
    const operator = operators.find(operator =>
      Array.isArray(operator.route_ids) &&
      operator.route_ids.some(id => stop.route_ids.includes(id))
    )

    const logoUrl = operator?.shadow_logo_url?.url ||
      operator?.logo_url?.url ||
      operator?.img_url?.url ||
      null

    stop.logo_url = logoUrl ? [logoUrl] : []
    stop.operator_ids = operator ? [operator.id] : []
  })

  return filteredStops
}

export function getSelectedPlannerMapOperators(operators, operatorOptions, selectedOperators) {
  return operators.filter(operator => {
    const option = operatorOptions.find(item => String(item.id) === String(operator.agency_id))

    return !option || selectedOperators[option.value] !== false
  })
}

export function isPlannerBikeOperatorSelected(selectedOperators, operatorOptions) {
  const bikeOption = operatorOptions.find(option => (
    option.id === '-' || String(option.value).replace(/[-\s]/g, '').toUpperCase() === 'VALENBISI'
  ))
  const selectedKey = bikeOption?.value || Object.keys(selectedOperators).find(key => (
    key.replace(/[-\s]/g, '').toUpperCase() === 'VALENBISI'
  ))

  return !selectedKey || selectedOperators[selectedKey] !== false
}

export function formatPlannerPhotonAddress(properties) {
  if (!properties) { return '' }

  if (properties.type == 'house') {
    return [properties.street || properties.name, properties.locality, properties.city]
      .filter(Boolean)
      .join(', ')
  }

  if (properties.type == 'street') {
    return [properties.name || properties.street, properties.locality, properties.city]
      .filter(Boolean)
      .join(', ')
  }

  return [
    [properties.postcode, properties.city].filter(Boolean).join(' '),
    properties.country
  ].filter(Boolean).join(', ')
}

export function formatPlannerMapCoordinates(lat, lng) {
  return `${lat},${lng}`
}

export function buildPlannerMapDirectionSelection(currentWaypoints, directionsSelected, latitude, longitude) {
  return {
    input: currentWaypoints,
    direction: directionsSelected,
    cordenate: {
      lat: latitude,
      lng: longitude
    }
  }
}

export function buildPlannerStopFavoritesParams(municipalityId) {
  return {
    municipality_id: municipalityId,
    order_by: 'code',
    order_dir: 'asc',
    per: 0,
    template: 'complete'
  }
}

export function buildPlannerStopsLoadOptions(municipality, pointerPosition, distance) {
  return {
    administrative_area_id: municipality.administrative_area_id,
    municipality_id: municipality.id,
    coordinates: [pointerPosition.lng, pointerPosition.lat],
    distance
  }
}
